import { checkResourceScope, ResourceScopeProbeError } from '@/features/auth/services/resourceScopeApi'
import * as Sentry from '@sentry/react'
import { useEffect, useState } from 'react'

export type OrderResourceScopeState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'in_scope' }
  | { status: 'not_found' }
  | { status: 'unauthorized' }
  | { status: 'error'; message: string }

type CachedScope = {
  orderId: string
  state: OrderResourceScopeState
}

/**
 * Preflight org-scope check for an order before loading order APIs or rendering deep-link UI.
 * Skips the probe when `orderId` is empty.
 *
 * Scope results are keyed by `orderId`. When the id changes (same route, new param), the hook
 * returns `loading` synchronously until the probe for the new id completes — avoiding a frame
 * where a prior `in_scope` would render children for the wrong order.
 */
export function useOrderResourceScope(orderId: string | undefined): OrderResourceScopeState {
  const id = orderId?.trim() ?? ''

  const [cached, setCached] = useState<CachedScope>(() =>
    id ? { orderId: id, state: { status: 'loading' } } : { orderId: '', state: { status: 'idle' } }
  )

  useEffect(() => {
    if (!id) {
      setCached({ orderId: '', state: { status: 'idle' } })
      return
    }

    let cancelled = false
    setCached({ orderId: id, state: { status: 'loading' } })

    void (async () => {
      try {
        const result = await checkResourceScope('order', id)
        if (cancelled) return
        setCached({
          orderId: id,
          state: result === 'in_scope' ? { status: 'in_scope' } : { status: 'not_found' },
        })
      } catch (err) {
        if (cancelled) return
        if (err instanceof ResourceScopeProbeError && err.status === 401) {
          setCached({ orderId: id, state: { status: 'unauthorized' } })
          return
        }
        const message = err instanceof Error ? err.message : 'Unable to verify order access'
        setCached({ orderId: id, state: { status: 'error', message } })
        if (!(err instanceof ResourceScopeProbeError)) {
          Sentry.captureException(err, { tags: { operation: 'auth.resource-scope.order' } })
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [id])

  if (!id) {
    return { status: 'idle' }
  }

  if (cached.orderId !== id) {
    return { status: 'loading' }
  }

  return cached.state
}
